/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, forwardRef, Inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router, UrlTree } from '@angular/router';
import { uniqueId } from 'lodash';
import { NavLink } from './nav-link';
import { NavTabBarComponent } from './nav-tab-bar.component';

/**
 * gh-nav-tab represents separate navigation tab.
 * Content of this component renders as tab caption.
 */
@Component({
  selector: 'gh-nav-tab',
  template: '<ng-template><ng-content></ng-content></ng-template>',
})
export class NavTabComponent implements OnInit {
  /**
   * Tab name simplifies routing when developer needs to navigate to separate tab.
   * Tab name should be unique among all tabs of gh-nav-tab-bar components.
   */
  @Input() name: string;

  /**
   * Allows to set query parameters when navigation is based on native angular routing
   */
  @Input() queryParams: Params;

  /**
   * Allows to set this tab as default.
   */
  @Input('default') useByDefault: boolean;

  /**
   * Template for internal content. It is necessary to display it later in the navigation tab.
   */
  @ViewChild(TemplateRef, { static: true }) templateRef: TemplateRef<any>;

  private _visible: boolean = true;
  private _link: NavLink;
  private commands?: any[];

  // tslint:disable-next-line:no-forward-ref
  constructor(@Inject(forwardRef(() => NavTabBarComponent)) private container: NavTabBarComponent,
              private route: ActivatedRoute,
              private router: Router) {

  }

  /**
   * Sets link for native angular routing
   */
  @Input()
  set link(data: any[] | string) {
    this.commands = Array.isArray(data) ? data : [data];
  }

  /**
   * Sets whether this tab is visible
   */
  @Input()
  set visible(visible: boolean) {
    if (this._visible !== visible) {
      this._visible = visible;

      if (this._link) {
        this._link.visible = visible;

        this.container.updateActiveLink();
      }
    }
  }

  /**
   * Returns links object represented by this component
   */
  getNavLink(): NavLink {
    return this._link;
  }

  ngOnInit(): void {
    if (!this.name) {
      this.name = uniqueId('navTab');
    }

    this._link = new NavLink(this.name, this.templateRef, this.createUrlTree(), this.useByDefault, this._visible);
  }

  private createUrlTree(): Maybe<UrlTree> {
    if (this.commands) {
      return this.router.createUrlTree(this.commands, {
        relativeTo: this.route,
        queryParams: this.queryParams,
      });
    }
  }
}
